import { useEffect } from "react";
import { useHistory } from "react-router";

/**
 * Tracks how deep the user is in the SPA navigation stack relative to the page
 * the webview was opened on (the deeplink entry point).
 *
 * Why we track a counter instead of reading `window.history.length`:
 * - In the Nubank iOS/Android webview, `window.history.length` can include
 *   browser history that existed before the webview was opened, so it is not a
 *   reliable signal for "is this the page the user landed on".
 * - React Router navigation does not always map 1:1 to `window.history.length`.
 *
 * `depth === 0` means we are still on the entry page (e.g. arrived via a Price
 * Alert push deeplink). In that state `history.goBack()` is a no-op, so the
 * back button must close the native webview instead. See NU-253 / NVH-733.
 */
let depth = 0;

export const incrementNavDepth = (): void => {
  depth += 1;
};

export const decrementNavDepth = (): void => {
  depth = Math.max(0, depth - 1);
};

export const resetNavDepth = (): void => {
  depth = 0;
};

export const getNavDepth = (): number => depth;

/**
 * True when the user is on the page the webview was opened on (no in-app
 * history to return to).
 */
export const isAtInitialPage = (): boolean => depth === 0;

/**
 * Mounts a navigation listener that keeps the SPA navigation depth in sync.
 * Call once, high in the tree, inside the Router. PUSH increases depth, POP
 * decreases it, REPLACE leaves it unchanged.
 */
export const useNavDepthTracker = (): void => {
  const history = useHistory();

  useEffect(() => {
    resetNavDepth();

    const unlisten = history.listen((_location, action) => {
      if (action === "PUSH") {
        incrementNavDepth();
      } else if (action === "POP") {
        decrementNavDepth();
      }
    });

    return () => {
      unlisten();
      resetNavDepth();
    };
  }, [history]);
};
